import 'reflect-metadata'

import { RequireProperty } from '@agroone/helpers'
import { plainToInstance, Type } from 'class-transformer'
import {
  IsArray,
  IsBoolean,
  IsDateString,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  MaxLength,
  Min,
  Validate,
  ValidateNested,
  ValidatorConstraint,
  ValidatorConstraintInterface,
} from 'class-validator'

@ValidatorConstraint({ name: 'isFutureDateString', async: false })
class IsFutureDateStringConstraint implements ValidatorConstraintInterface {
  validate(value?: string): boolean {
    if (!value) {
      return true
    }

    const selectedDate = new Date(value)
    if (Number.isNaN(selectedDate.getTime())) {
      return false
    }

    selectedDate.setHours(0, 0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return selectedDate > today
  }

  defaultMessage(): string {
    return '$property must be a future date'
  }
}

export class SavePesticideIngredient {
  @IsInt()
  @IsPositive()
  @IsOptional()
  id?: number

  @IsString()
  @IsNotEmpty()
  name: string | undefined

  @IsNumber()
  @Min(0)
  concentration: number | undefined

  @IsString()
  @IsNotEmpty()
  concentrationUnit: string | undefined
}

export class SaveReentry {
  @IsInt()
  @IsPositive()
  @IsOptional()
  id?: number

  @IsNumber()
  @RequireProperty('reentryUnit')
  @Min(0)
  reentryInterval: number | undefined

  @IsString()
  @RequireProperty('reentryInterval')
  @IsNotEmpty()
  reentryUnit: string | undefined

  @IsOptional()
  @IsNumber()
  @Min(0)
  interventionMinValue?: number

  @IsOptional()
  @IsString()
  interventionMinUnit?: string

  @IsOptional()
  @IsNumber()
  @Min(0)
  applicationNumber?: number

  @IsOptional()
  @IsString()
  fieldType?: string
}

export class SavePesticideTargetCropType {
  @IsInt()
  @IsPositive()
  @IsOptional()
  id?: number

  @IsNumber()
  @IsNotEmpty()
  cropTypeId: number | undefined

  @IsString()
  @RequireProperty('preharvestIntervalUnit')
  preharvestInterval: string | undefined

  @IsNumber()
  @RequireProperty('preharvestIntervalUnit')
  @Min(0)
  preharvestIntervalMax?: number

  @IsString()
  @RequireProperty('preharvestInterval')
  @IsNotEmpty()
  preharvestIntervalUnit: string | undefined

  @IsNumber()
  @RequireProperty('doseUnit')
  @Min(0)
  maxDose: number | undefined

  @IsNumber()
  @IsInt()
  @IsOptional()
  passageNumber?: number

  @IsNumber()
  @IsOptional()
  cumulative?: number

  @IsString()
  @RequireProperty('maxDose')
  @IsNotEmpty()
  // @toimprove DoseUnit not tested if it exists for now
  doseUnit: string | undefined

  @IsOptional()
  @IsDateString()
  @Validate(IsFutureDateStringConstraint)
  lastUse?: string

  @IsOptional()
  @IsBoolean()
  exemption?: boolean

  @IsOptional()
  @IsDateString()
  registrationExpiration?: string

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SaveReentry)
  reentries: SaveReentry[] | undefined
}

export class SavePesticide {
  public static build(raw: any) {
    return plainToInstance(SavePesticide, raw)
  }

  @IsInt()
  @IsPositive()
  @IsOptional()
  id?: number

  @IsString()
  @IsNotEmpty()
  name!: string

  @IsString()
  @IsNotEmpty()
  manufacturer!: string

  @IsString()
  @IsNotEmpty()
  identificationNumber!: string

  @IsNumber()
  @IsNotEmpty()
  countryId!: number

  @IsString()
  @IsNotEmpty()
  pesticideType!: string

  @IsBoolean()
  @IsNotEmpty()
  homologa!: boolean

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SavePesticideIngredient)
  activeIngredients: SavePesticideIngredient[] | undefined

  @IsOptional()
  @IsString()
  @MaxLength(500)
  productInfo?: string

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SavePesticideTargetCropType)
  targetCrops: SavePesticideTargetCropType[] | undefined
}
